import React, { useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  SelectChangeEvent,
} from '@mui/material';


const TempAccount: React.FC = () => {
  // 年、月、日それぞれの候補を生成
  const years = Array.from({ length: 60 }, (_, i) => new Date().getFullYear() - i); // 直近60年分
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  const initialFormData = {
    firstName: '',
    middleName: '',
    lastName: '',
    birthYear: '',  // 生まれた年
    birthMonth: '', // 生まれた月
    birthDay: '',   // 生まれた日
    sex: '',
    address: '',
    tel: '',
    mobile: '',
    email: '',
    qualifications: '',
    skills: '',
    history: '',
    completionDate: '',
    photoProfile: null as File | null,
    photoLicense: null as File | null,
  };

  const [formData, setFormData] = useState(initialFormData);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSelectChange = (field: string) => (event: SelectChangeEvent) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleFileChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, [field]: file }));
  };



  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    // 必須項目チェック (例として firstName, lastName, birthYear, birthMonth, birthDay, sex, mobile)
    const requiredFields = ['firstName', 'lastName', 'birthYear', 'birthMonth', 'birthDay', 'sex', 'mobile'];
    for (const field of requiredFields) {
      if (!formData[field as keyof typeof formData]) {
        alert(`The field "${field}" is required.`);
        return;
      }
    }

    // ファイル送信用 FormData
    const formDataToSend = new FormData();
    // 例: "dob" を組み立てて一つの値としてサーバーに送る(yyyy-mm-ddなど)
    const dobStr = `${formData.birthYear}-${formData.birthMonth}-${formData.birthDay}`;

    formDataToSend.append('firstName', formData.firstName);
    formDataToSend.append('middleName', formData.middleName);
    formDataToSend.append('lastName', formData.lastName);
    formDataToSend.append('dob', dobStr);
    formDataToSend.append('sex', formData.sex);
    formDataToSend.append('address', formData.address);
    formDataToSend.append('tel', formData.tel);
    formDataToSend.append('mobile', formData.mobile);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('qualifications', formData.qualifications);
    formDataToSend.append('skills', formData.skills);
    formDataToSend.append('history', formData.history);
    formDataToSend.append('completionDate', formData.completionDate);

    if (formData.photoProfile) {
      formDataToSend.append('photoProfile', formData.photoProfile);
    }
    if (formData.photoLicense) {
      formDataToSend.append('photoLicense', formData.photoLicense);
    }

    // 実際の送信
    fetch('https://xs525443.xsrv.jp/kansaibou/dr_add_api.php', {
      method: 'POST',
      body: formDataToSend,
    })
      .then((response) => response.json())
      .then((responseData) => {
        console.log('Success:', responseData);
        alert('Registration successful.');
        setFormData(initialFormData);
      })
      .catch((error) => {
        console.error('Error:', error);
        alert('An error occurred during registration. Please try again.');
      });
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Doctor Registration
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          {/* Name fields */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="First Name *"
              fullWidth
              onChange={handleChange('firstName')}
              value={formData.firstName}
            />
            <TextField
              label="Middle Name"
              fullWidth
              onChange={handleChange('middleName')}
              value={formData.middleName}
            />
          </Box>
          <TextField
            label="Last Name *"
            fullWidth
            sx={{ mt: 2 }}
            onChange={handleChange('lastName')}
            value={formData.lastName}
          />

          {/* DOB (Year/Month/Day) */}
          <Typography sx={{ mt: 2 }}>Date of Birth *</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={formData.birthYear}
                label="Year"
                onChange={handleSelectChange('birthYear')}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Month</InputLabel>
              <Select
                value={formData.birthMonth}
                label="Month"
                onChange={handleSelectChange('birthMonth')}
              >
                {months.map((m) => (
                  <MenuItem key={m} value={String(m).padStart(2, '0')}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Day</InputLabel>
              <Select
                value={formData.birthDay}
                label="Day"
                onChange={handleSelectChange('birthDay')}
              >
                {days.map((d) => (
                  <MenuItem key={d} value={String(d).padStart(2, '0')}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Sex (Male / Female) */}
          <Typography sx={{ mt: 2 }}>Sex *</Typography>
          <RadioGroup
            row
            value={formData.sex}
            onChange={(e) => setFormData((prev) => ({ ...prev, sex: e.target.value }))}
          >
            <FormControlLabel value="Male" control={<Radio />} label="Male" />
            <FormControlLabel value="Female" control={<Radio />} label="Female" />
          </RadioGroup>

          {/* Address / Tel / Mobile / Email */}
          <TextField
            label="Address *"
            fullWidth
            sx={{ mt: 2 }}
            onChange={handleChange('address')}
            value={formData.address}
          />
          <TextField
            label="Tel (Optional)"
            fullWidth
            sx={{ mt: 2 }}
            onChange={handleChange('tel')}
            value={formData.tel}
          />
          <TextField
            label="Mobile *"
            fullWidth
            sx={{ mt: 2 }}
            onChange={handleChange('mobile')}
            value={formData.mobile}
          />
          <TextField
            label="Email"
            fullWidth
            sx={{ mt: 2 }}
            onChange={handleChange('email')}
            value={formData.email}
          />

          {/* Profile Photo / License Photo */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1">Profile Photo</Typography>
            <input type="file" accept="image/*" onChange={handleFileChange('photoProfile')} />
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1">License Photo</Typography>
            <input type="file" accept="image/*" onChange={handleFileChange('photoLicense')} />
          </Box>

          {/* Qualifications, Skills, History */}
          <TextField
            label="Qualifications Obtained"
            fullWidth
            multiline
            rows={4}
            sx={{ mt: 3 }}
            onChange={handleChange('qualifications')}
            value={formData.qualifications as string}
          />
          <TextField
            label="Clinical / Procedural Skills"
            fullWidth
            multiline
            rows={4}
            sx={{ mt: 3 }}
            onChange={handleChange('skills')}
            value={formData.skills as string}
          />
          <TextField
            label="Practicing History"
            fullWidth
            multiline
            rows={4}
            sx={{ mt: 3 }}
            onChange={handleChange('history')}
            value={formData.history as string}
          />

          {/* Completion Date */}
          <Typography sx={{ mt: 3 }}>Completion Date</Typography>
          <TextField
            type="date"
            fullWidth
            sx={{ mt: 1 }}
            onChange={handleChange('completionDate')}
            value={formData.completionDate as string}
            InputLabelProps={{ shrink: true }}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2 }}
            fullWidth
          >
            Register
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default TempAccount;
